import type { Domain } from './portal';

import { buildUrl, getPortalDomain } from './portal';

/**
 * Get the current app's domain key based on VITE_APP_BASE_URL
 */
export function getCurrentAppDomain(): Domain {
    const appBaseUrl = import.meta.env.VITE_APP_BASE_URL as string;

    if (appBaseUrl.includes('elektra')) {
        return 'elektra-app';
    }

    return 'portaal';
}

/**
 * Check if a path contains a project ID (UUID format)
 * Used to determine if SSO redirect to elektra-app is needed
 *
 * @example
 * isProjectPath('/projects/aadd36b7-970d-4caa-b141-dc00521bfd26') // true
 * isProjectPath('/settings') // false
 */
export function isProjectPath(path: string): boolean {
    const projectPathRegex = /\/projects\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    return projectPathRegex.test(path);
}

export type LoginOptions = {
    /**
     * Path to redirect to after login (defaults to current path)
     */
    redirectPath?: string;

    /**
     * Optional async callback called before redirect.
     * Return `true` to proceed with redirect, `false` to cancel.
     *
     * Use this to:
     * - Check for pending mutations that need syncing
     * - Show a modal to the user before redirecting
     * - Perform cleanup before navigation
     *
     * @example
     * navigateToLogin({
     *     onBeforeRedirect: async () => {
     *         if (hasPendingSync()) {
     *             showSyncRequiredModal();
     *             return false; // Cancel redirect, let modal handle it
     *         }
     *         return true; // Proceed with redirect
     *     }
     * });
     */
    onBeforeRedirect?: () => boolean | Promise<boolean>;
};

/**
 * Build the login URL for redirecting to portaal login
 *
 * @param redirectPath - Optional path to redirect to after login (defaults to current path)
 * @returns The full login URL with search params
 */
export function getLoginUrl(redirectPath?: string): string {
    const portalDomain = getPortalDomain();
    const from = getCurrentAppDomain();
    const path = redirectPath ?? (window.location.pathname + window.location.search);

    const url = new URL('/login', buildUrl(portalDomain));
    url.searchParams.set('from', from);
    url.searchParams.set('redirectPath', path);

    return url.toString();
}

/**
 * Navigate to portaal login page with correct context for SSO redirect back
 *
 * @param options - Optional configuration for the redirect
 * @param options.redirectPath - Path to redirect to after login
 * @param options.onBeforeRedirect - Callback to run before redirect, return false to cancel
 *
 * @example
 * // Simple redirect
 * navigateToLogin();
 *
 * // With guard callback
 * navigateToLogin({
 *     onBeforeRedirect: async () => {
 *         if (needsSync) {
 *             showModal();
 *             return false;
 *         }
 *         return true;
 *     }
 * });
 */
export async function navigateToLogin(options?: LoginOptions): Promise<void> {
    const { redirectPath, onBeforeRedirect } = options ?? {};

    // If callback provided, check if we should proceed
    if (onBeforeRedirect) {
        const shouldRedirect = await onBeforeRedirect();
        if (!shouldRedirect) {
            return; // Callback cancelled the redirect
        }
    }

    window.location.href = getLoginUrl(redirectPath);
}
